import { type AxiosError, type AxiosResponse } from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { ActionKeys } from '~/actions/constants';
import * as trackingSettingsActions from '~/actions/tracking-settings.actions';
import * as userActions from '~/actions/user.actions';
import { getTrackingConfig } from '~/selectors/tracking-settings.selectors';
import { getCompanySettings } from '~/selectors/user.selectors';
import * as trackingSettingsService from '~/services/tracking-settings.service';
import * as userService from '~/services/user.service';
import * as Company from '~/types/Company';
import * as TrackingSettings from '~/types/TrackingSettings';

function* fetchTrackingConfig() {
  try {
    const { data }: AxiosResponse<TrackingSettings.JSONTrackingConfig> = yield call(
      trackingSettingsService.fetchTrackingConfig,
    );
    yield put(trackingSettingsActions.fetchTrackingConfigSuccess(TrackingSettings.fromJSON(data)));
  } catch (err) {
    const axiosError = err as AxiosError;
    yield put(trackingSettingsActions.fetchTrackingConfigError(axiosError?.response?.data?.detail));
  }
}

function* updateTrackingConfig(
  action: ReturnType<typeof trackingSettingsActions.updateTrackingConfig>,
) {
  try {
    const { data }: AxiosResponse<TrackingSettings.JSONTrackingConfig> = yield call(
      trackingSettingsService.updateTrackingConfig,
      TrackingSettings.toJSON(action.payload),
    );
    const parsedData = TrackingSettings.fromJSON(data);
    yield put(trackingSettingsActions.fetchTrackingConfigSuccess(parsedData));
    yield put(trackingSettingsActions.updateTrackingConfigSuccess(parsedData));
  } catch (err) {
    const axiosError = err as AxiosError;
    yield put(trackingSettingsActions.fetchTrackingConfigError(axiosError?.response?.data?.detail));
  }
}

function* updateBrandAndFooter(
  action: ReturnType<typeof trackingSettingsActions.updateBrandAndFooter>,
) {
  try {
    // api call to update Company brands
    // set company field values with redux store
    const company = yield select(getCompanySettings);
    company.replyToEmail = action.payload.replyToEmail;

    const companyJSON = Company.toJSON(company);

    const { data: companyRespData }: AxiosResponse<Company.JSONCompany> = yield call(
      userService.updateCompany,
      companyJSON,
    );

    const parsedCompanyData = Company.fromJSON(companyRespData);
    yield put(userActions.updateCompanySuccess(parsedCompanyData));

    // make second api call to update Tracking configs
    const trackingConfig: TrackingSettings.TrackingConfig = yield select(getTrackingConfig);
    // set required fields for trackingConfig payload
    const trackingConfigPayload = TrackingSettings.setTrackingConfigPayload(
      trackingConfig,
      action.payload,
    );

    const trackingConfigJSON = TrackingSettings.toJSON(trackingConfigPayload);

    const { data: trackingConfigRespData }: AxiosResponse<TrackingSettings.JSONTrackingConfig> =
      yield call(trackingSettingsService.updateTrackingConfig, trackingConfigJSON);

    const parsedTrackingConfigData = TrackingSettings.fromJSON(trackingConfigRespData);

    // update trackngConfig store with updated values
    yield put(trackingSettingsActions.fetchTrackingConfigSuccess(parsedTrackingConfigData));

    // update Brand and Footer request on Success
    yield put(
      trackingSettingsActions.updateBrandAndFooterSuccess({
        ...parsedTrackingConfigData,
        replyToEmail: parsedCompanyData.replyToEmail,
      }),
    );
  } catch (err) {
    const axiosError = err as AxiosError;
    yield put(
      trackingSettingsActions.updateBrandAndFooterError(axiosError?.response?.data?.detail),
    );
  }
}

export default [
  takeLatest(ActionKeys.FETCH_TRACKING_CONFIG, fetchTrackingConfig),
  takeLatest(ActionKeys.UPDATE_TRACKING_CONFIG, updateTrackingConfig),
  takeLatest(ActionKeys.UPDATE_BRAND_AND_FOOTER_SETTINGS, updateBrandAndFooter),
];
